/**
 * Derives the admin-grant `ref` (`GrantSpec.ref`) that reproduces `url` under the Worker's
 * `catalogUrlForGrant` convention (`worker/src/catalogs.ts`):
 *
 *   pack:    `<base>/<ref>/manifest.json`
 *   catalog: `<base>/<ref>/catalog.json`
 *
 * — i.e. `deriveGrantRef` is the exact inverse of `catalogUrlForGrant`. Not literally shared
 * code (`worker/` is a standalone package per CLAUDE.md, so `@equipped/core` can't import
 * it) — this is a hand-kept mirror; `grantRef.test.ts` pins fixtures matching
 * `worker/test/catalogs.test.ts`'s own `catalogUrlForGrant` examples so a convention change
 * on either side is caught on review, not silently at runtime.
 *
 * A catalog/pack's own declared `id` need not match its actual R2 path segment (e.g. a
 * catalog document's `id` can drift from the folder it's actually served from), so the ref
 * must be derived from the real fetch URL (`CatalogPackEntry.manifestUrl` /
 * `CatalogNode.entryUrl`), never guessed from `id` — see
 * docs/263-PROVIDER-PAGES-AND-INVITE-ACCESS.md.
 *
 * Returns `null` when `url` isn't derivable under `baseUrl` — absent (e.g. an
 * `entry.inline` sub-catalog has no URL at all), no shared prefix, or the expected trailing
 * filename is missing. A `null` ref means "not independently grantable" — the caller should
 * skip/disable that target, never fall back to a guessed ref.
 */
export function deriveGrantRef(
  baseUrl: string,
  kind: "pack" | "catalog",
  url: string | undefined,
): string | null {
  if (!url) return null;
  const base = baseUrl.replace(/\/+$/, "");
  if (!base) return null;
  const file = kind === "catalog" ? "catalog.json" : "manifest.json";
  const prefix = `${base}/`;
  const suffix = `/${file}`;
  if (!url.startsWith(prefix) || !url.endsWith(suffix)) return null;
  const ref = url.slice(prefix.length, url.length - suffix.length).replace(/^\/+|\/+$/g, "");
  return ref || null;
}
